import type { EChartsOption } from "echarts";
import { useContext, useMemo, useRef, useState } from "react";
import DashboardCardHeader from "../DashboardCardHeader";
import { Box, CircularProgress, Container } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import {
  BaseErrorCard,
  BaseUsinaValue,
} from "../../models/dados-indicadores.model";
import { DashboardContext } from "../../context/DashboardContexProvider";
import { EmptyTable } from "../../../clients/components/EmptyState";
import { CardTypesEnum } from "../../types";
import { getCardIsLoading } from "../TextCardContent";
import { buildAccumulatedEnergyChartSeries } from "../../helpers/buildAccumulatedEnergyChartSeries";

export type AccumulatedEnergyValueAndDateTime = {
  dateTime: string;
  value: number;
};

export type AccumulatedEnergyProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: {
    accumulatedIdealEnergy?: BaseUsinaValue[];
    accumulatedGeneratedEnergy?: BaseUsinaValue[];
    pAccumulated?: BaseUsinaValue[];
    cardError: BaseErrorCard;
  };
};

function hasNonNullValue(values: (number | null)[]): boolean {
  return values.some((value) => value != null);
}

export default function AccumulatedEnergyChart({
  cardId,
  data,
  title,
  lastUpdate,
  cardType,
}: AccumulatedEnergyProps) {
  const chartAccumulatedEnergy = useRef(null);
  const [isMouseIn, setIsMouseIn] = useState<boolean>(false);
  const {
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);

  const [energiaIdealColor, setEnergiaIdealColor] = useState<string>(
    cardsDisplayableColorProps.energiaAcumulada.energiaIdealColor ?? "#EE7D00"
  );
  const [energiaGeradaColor, setEnergiaGeradaColor] = useState<string>(
    cardsDisplayableColorProps.energiaAcumulada.energiaGeradaColor ?? "#FAB900"
  );
  const [p50Color, setP50Color] = useState<string>(
    cardsDisplayableColorProps.energiaAcumulada.P50Color ?? "#FADE0A"
  );

  const headerTitle = title ?? "Energia Acumulada (MWh)";

  const chartSeries = useMemo(
    () => buildAccumulatedEnergyChartSeries(data),
    [
      data.accumulatedIdealEnergy,
      data.accumulatedGeneratedEnergy,
      data.pAccumulated,
    ],
  );

  const {
    xLabels,
    idealValues: accumulatedIdealEnergyValues,
    generatedValues: accumulatedGeneratedEnergyValues,
    p50Values: pAccumulatedValues,
    collectDay,
  } = chartSeries;

  const option: EChartsOption = useMemo(
    () => ({
      backgroundColor: "transparent",
      title: {
        text: collectDay,
        textStyle: {
          fontSize: 14,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        confine: true,
      },
      legend: {
        top: "5%",
        data: ["Energia Ideal", "Energia Gerada", "P50"],
      },
      xAxis: {
        data: xLabels,
      },
      yAxis: {},
      series: [
        {
          name: "Energia Ideal",
          type: "line",
          data: accumulatedIdealEnergyValues,
          itemStyle: {
            color: energiaIdealColor,
          },
        },
        {
          name: "Energia Gerada",
          type: "line",
          data: accumulatedGeneratedEnergyValues,
          itemStyle: {
            color: energiaGeradaColor,
          },
        },
        {
          name: "P50",
          type: "line",
          data: pAccumulatedValues,
          itemStyle: {
            color: p50Color,
          },
        },
      ],
    }),
    [
      collectDay,
      xLabels,
      accumulatedIdealEnergyValues,
      accumulatedGeneratedEnergyValues,
      pAccumulatedValues,
      energiaIdealColor,
      energiaGeradaColor,
      p50Color,
    ],
  );

  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;

  const [openModal, setopenModal] = useState<boolean>(false);

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      energiaAcumulada: {
        energiaGeradaColor: energiaGeradaColor,
        energiaIdealColor: energiaIdealColor,
        P50Color: p50Color,
      },
    });
    setopenModal(false);
  };

  const hasData =
    xLabels.length > 0 &&
    (hasNonNullValue(pAccumulatedValues) ||
      hasNonNullValue(accumulatedGeneratedEnergyValues) ||
      hasNonNullValue(accumulatedIdealEnergyValues));

  return (
    <>
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          cardId={cardId}
          elementRef={chartAccumulatedEnergy}
          isMouseIn={isMouseIn}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          onConfirmColorsModal={handleConfirmModal}
          colorButton
          colorsOptions={[
            {
              title: "Energia Ideal",
              value: energiaIdealColor,
              setValue: setEnergiaIdealColor,
            },
            {
              title: "Energia Gerada",
              value: energiaGeradaColor,
              setValue: setEnergiaGeradaColor,
            },

            {
              title: "P50",
              value: p50Color,
              setValue: setP50Color,
            },
          ]}
          cardType={CardTypesEnum.EnergiaAcumulada}
          hasFilter
        />

        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData ? (
            <Container
              ref={chartAccumulatedEnergy}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "90%",
              }}
            >
              <RopChart option={option} />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable title="Nenhum dado encontrado!" />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </div>
    </>
  );
}
